import { QUESTIONS, SEPARATOR, createReader, fieldText, findFieldValue, formatDosage, unique } from './documentData'

// IDs das perguntas usadas só na Guia de Aplicação
export const GUIDE_QUESTIONS = {
  cubicMeters: 339, // filha (cálculo)
  productQuantity: 338, // filha (cálculo)
  holds: 1001, // filha (seleção múltipla)
  // perguntas alternativas de tempo de exposição (uma por lógica); vale a primeira respondida
  exposureTimes: [179, 314, 321, 1004]
}

const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August'].concat([
  'September',
  'October',
  'November',
  'December'
])
const MONTHS_PT = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto'].concat([
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro'
])

// Opções da pergunta 130 (destino) já estão em inglês; aqui só a tradução para o português
export const DESTINATIONS_PT = {
  ALGERIA: 'ARGÉLIA',
  'ARAB EMIRATES': 'EMIRADOS ÁRABES',
  AUSTRALIA: 'AUSTRÁLIA',
  COLOMBIA: 'COLÔMBIA',
  DENMARK: 'DINAMARCA',
  'DOMINICAN REPUBLIC': 'REPÚBLICA DOMINICANA',
  ECUADOR: 'EQUADOR',
  EGYPT: 'EGITO',
  EUROPE: 'EUROPA',
  FRANCE: 'FRANÇA',
  GERMANY: 'ALEMANHA',
  INDIA: 'ÍNDIA',
  INDONESIA: 'INDONÉSIA',
  IRAN: 'IRÃ',
  IRAQ: 'IRAQUE',
  ITALY: 'ITÁLIA',
  JAPAN: 'JAPÃO',
  MALAYSIA: 'MALÁSIA',
  MEXICO: 'MÉXICO',
  MYANMAR: 'MIANMAR',
  NETHERLANDS: 'PAÍSES BAIXOS',
  'NEW ZEALAND': 'NOVA ZELÂNDIA',
  'NORTH KOREA': 'COREIA DO NORTE',
  'NORTHERN IRELAND': 'IRLANDA DO NORTE',
  PAKISTAN: 'PAQUISTÃO',
  RUSSIA: 'RÚSSIA',
  SLOVENIA: 'ESLOVÊNIA',
  'SOUTH KOREA': 'COREIA DO SUL',
  SPAIN: 'ESPANHA',
  TBC: 'A CONFIRMAR',
  THAILAND: 'TAILÂNDIA',
  'TRINITY AND TOBAGO': 'TRINIDAD E TOBAGO',
  TURKEY: 'TURQUIA',
  UKRAINE: 'UCRÂNIA',
  'UNITED KINGDOM': 'REINO UNIDO',
  'UNITED STATES OF AMERICA': 'ESTADOS UNIDOS DA AMÉRICA',
  VIETNAM: 'VIETNÃ'
}

// Opções da pergunta 169 (mercadoria) estão em português; aqui a tradução para o inglês
export const GOODS_EN = {
  SOJA: 'SOYBEANS',
  'FARELO DE SOJA': 'SOYBEAN MEAL',
  MILHO: 'MAIZE',
  TRIGO: 'WHEAT',
  SORGO: 'SORGHUM',
  DDGS: 'DDGS',
  ARROZ: 'RICE'
}

// Produtos comerciais (pergunta 1005): [inglês, português]
const PRODUCT_SUFFIX = { PHOSTEK: [' TABLETS', ' PASTILHAS'] }

export const translate = (dictionary, text) => dictionary[text] || text

// 7674.92 -> "7.674,92" (ou "7.674,920" com 3 casas)
export const formatNumber = (value, decimals = 2) => {
  const number = Number(value)
  return value === '' || Number.isNaN(number)
    ? value
    : number.toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

export const sortNumerically = text =>
  text
    .split(', ')
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    .join(', ')

// "<endereço> Cnpj: <cnpj> - Tel <telefone> - E-Mail: <e-mail>" (omite o que estiver vazio)
export const formatInterestedDetails = fieldValues => {
  const field = name => fieldText(findFieldValue(fieldValues, name))
  const contacts = [
    field('CNPJ') && `Cnpj: ${field('CNPJ')}`,
    field('Telefone') && `Tel ${field('Telefone')}`,
    field('E-mail') && `E-Mail: ${field('E-mail')}`
  ]
    .filter(Boolean)
    .join(' - ')
  return [field('Endereço'), contacts].filter(Boolean).join(' ')
}

// Mês e ano da conclusão da atividade, em inglês e em português (vazios se não houver data)
export const formatCompletionDate = endDate => {
  const completedAt = new Date(endDate)
  if (!endDate || Number.isNaN(completedAt.getTime())) return { en: '', pt: '', monthYear: { en: '', pt: '' } }
  const [month, year] = [completedAt.getMonth(), completedAt.getFullYear()]
  return {
    en: `${MONTHS_EN[month]}, ${year}`,
    pt: `de ${MONTHS_PT[month]} de ${year}`,
    monthYear: { en: `${MONTHS_EN[month]} / ${year}`, pt: `${MONTHS_PT[month]} / ${year}` }
  }
}

export const buildGuideData = ({ serviceOrder, childrenServiceOrders = [], assets = [] }) => {
  const { answers, answer, joined, treatmentPlace, treatmentAddress } = createReader({
    serviceOrder,
    childrenServiceOrders,
    assets
  })

  const completionDate = formatCompletionDate(serviceOrder.endDate)

  const goods = answer(QUESTIONS.goods).toUpperCase()
  const destination = answer(QUESTIONS.destination).toUpperCase()
  const dosage = unique(answers(...QUESTIONS.dosages).map(formatDosage)).join(SEPARATOR)
  const exposureTime = GUIDE_QUESTIONS.exposureTimes.map(answer).find(Boolean)
  const products = unique(answers(QUESTIONS.product).map(name => name.toUpperCase())).map(name => [
    name + (PRODUCT_SUFFIX[name]?.[0] || ''),
    name + (PRODUCT_SUFFIX[name]?.[1] || '')
  ])

  return {
    activityCode: serviceOrder.yearlyId || '',
    communicationNumber: answer(QUESTIONS.communicationNumber),
    dateEn: completionDate.en,
    datePt: completionDate.pt,
    interested: serviceOrder.place?.name || '',
    interestedDetails: formatInterestedDetails(serviceOrder.place?.fieldValues),
    exporters: joined(QUESTIONS.exporter),
    ship: answer(QUESTIONS.ship).toUpperCase(),
    destinationEn: destination,
    destinationPt: translate(DESTINATIONS_PT, destination),
    place: [treatmentPlace, treatmentAddress].filter(Boolean).join('  '),
    goodsEn: translate(GOODS_EN, goods),
    goodsPt: goods,
    cubicMeters: formatNumber(answer(GUIDE_QUESTIONS.cubicMeters)),
    exposureTime: exposureTime ? `${exposureTime}hs` : '',
    dosageEn: dosage && `${dosage} gram/m³`,
    dosagePt: dosage && `${dosage} g/m³`,
    productQuantity: formatNumber(answer(GUIDE_QUESTIONS.productQuantity)),
    holds: sortNumerically(answer(GUIDE_QUESTIONS.holds)),
    productEn: products.map(([en]) => en).join(SEPARATOR),
    productPt: products.map(([, pt]) => pt).join(SEPARATOR),
    mapaPort: answer(QUESTIONS.mapaPort).toUpperCase()
  }
}
